import { Itinerary, JourneyPlannerItineraryTransportMode } from '@ridango/components';
import {
    JOURNEY_ITINERARY_REUSED_WCAG_TRANSLATION_KEYS,
    JOURNEY_ITINERARY_WCAG_TRANSLATION_KEYS
} from './journey-itinerary.const';
import { BuildJourneyAriaLabelParams } from './journey-itinerary.interface';

export class JourneyItineraryUtils {
    private static NEW_KEYS = JOURNEY_ITINERARY_WCAG_TRANSLATION_KEYS;
    private static REUSED_KEYS = JOURNEY_ITINERARY_REUSED_WCAG_TRANSLATION_KEYS;

    static buildJourneyAriaLabel(params: BuildJourneyAriaLabelParams): string {
        const {
            itinerary,
            realTimeStartTime,
            realTimeEndTime,
            boardAt,
            hasDeparted,
            isRealtime,
            locale,
            timezone,
            translate
        } = params;

        if (!itinerary?.legs?.length) {
            return '';
        }

        const firstLeg = itinerary.legs[0];
        const lastLeg = itinerary.legs[itinerary.legs.length - 1];
        const origin = this.resolveStopNameForAria(firstLeg?.from?.name, translate);
        const destination = this.resolveStopNameForAria(lastLeg?.to?.name, translate);
        const departureTime = this.formatTime(realTimeStartTime || itinerary.startTime, locale, timezone);
        const scheduledDepartureTime = this.formatTime(itinerary.startTime, locale, timezone);
        const arrivalTime = this.formatTime(realTimeEndTime || itinerary.endTime, locale, timezone);
        const duration = this.formatDuration(itinerary.duration, translate);

        const phrases: string[] = [];
        phrases.push(translate(this.NEW_KEYS.JOURNEY_FROM_TO, { origin, destination }));

        if (departureTime) {
            if (this.isDifferentMinute(realTimeStartTime, itinerary.startTime) && scheduledDepartureTime) {
                phrases.push(
                    translate(this.REUSED_KEYS.DEPARTURE_WITH_ORIGINAL, {
                        time: departureTime,
                        originalTime: scheduledDepartureTime
                    })
                );
            } else {
                phrases.push(translate(this.REUSED_KEYS.DEPARTURE, { time: departureTime }));
            }
        }

        if (arrivalTime) {
            phrases.push(translate(this.NEW_KEYS.JOURNEY_ARRIVAL, { time: arrivalTime }));
        }

        phrases.push(translate(this.NEW_KEYS.JOURNEY_TOTAL_DURATION, { duration }));

        if (boardAt && !hasDeparted) {
            phrases.push(
                translate(this.NEW_KEYS.JOURNEY_BOARD_FIRST, {
                    mode: this.getFirstBoardingModeLabel(itinerary, translate),
                    time: this.formatTime(boardAt, locale, timezone)
                })
            );
        }

        if (itinerary?.changes > 0) {
            phrases.push(
                translate(itinerary.changes > 1 ? this.NEW_KEYS.CHANGE_MANY : this.NEW_KEYS.CHANGE_ONE, {
                    count: itinerary.changes
                })
            );
        }

        const includes = this.buildIncludes(itinerary, translate);
        if (includes.length) {
            phrases.push(translate(this.NEW_KEYS.JOURNEY_INCLUDES, { includes: includes.join(', ') }));
        }

        if (isRealtime) {
            phrases.push(translate(this.NEW_KEYS.JOURNEY_LIVE_UPDATES));
        }

        return phrases.filter(Boolean).join(' ');
    }

    private static buildIncludes(itinerary: Itinerary, translate: BuildJourneyAriaLabelParams['translate']): string[] {
        if (!itinerary?.legs?.length) {
            return [];
        }

        const includes: string[] = [];
        const hasWalking = itinerary.legs.some((leg) => leg?.mode === JourneyPlannerItineraryTransportMode.Walk);
        if (hasWalking) {
            includes.push(translate(this.NEW_KEYS.MODE_WALKING));
        }

        const routeNames = Array.from(
            new Set(
                itinerary.legs
                    .filter((leg) => leg?.mode !== JourneyPlannerItineraryTransportMode.Walk)
                    .map((leg) => (leg?.routeShortName || leg?.route || '').trim())
                    .filter(Boolean)
            )
        );

        routeNames.forEach((routeName) => {
            includes.push(translate(this.NEW_KEYS.ROUTE_NAME, { route: routeName }));
        });

        return includes;
    }

    private static getFirstBoardingModeLabel(
        itinerary: Itinerary,
        translate: BuildJourneyAriaLabelParams['translate']
    ): string {
        const firstTransitLeg = itinerary?.legs?.find((leg) => leg?.mode !== JourneyPlannerItineraryTransportMode.Walk);
        const mode = firstTransitLeg?.mode;

        if (mode === JourneyPlannerItineraryTransportMode.Bus) {
            return translate(this.REUSED_KEYS.MODE_BUS);
        }
        if (mode === JourneyPlannerItineraryTransportMode.Tram) {
            return translate(this.REUSED_KEYS.MODE_TRAM);
        }
        if (mode === JourneyPlannerItineraryTransportMode.Rail) {
            return translate(this.REUSED_KEYS.MODE_TRAIN);
        }
        if (mode === JourneyPlannerItineraryTransportMode.Ferry) {
            return translate(this.REUSED_KEYS.MODE_FERRY);
        }
        if (mode === JourneyPlannerItineraryTransportMode.Trolleybus) {
            return translate(this.REUSED_KEYS.MODE_TROLLEYBUS);
        }
        if (mode === JourneyPlannerItineraryTransportMode.Flexible) {
            return translate(this.REUSED_KEYS.MODE_FLEXIBLE);
        }

        return translate(this.REUSED_KEYS.MODE_TRANSPORT);
    }

    private static formatDuration(
        durationInSeconds: number,
        translate: BuildJourneyAriaLabelParams['translate']
    ): string {
        const totalMinutes = Math.max(0, Math.round((durationInSeconds || 0) / 60));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const parts: string[] = [];

        if (hours > 0) {
            parts.push(
                `${hours} ${translate(hours === 1 ? this.NEW_KEYS.DURATION_HOUR : this.NEW_KEYS.DURATION_HOURS)}`
            );
        }

        if (minutes > 0 || hours === 0) {
            parts.push(
                `${minutes} ${translate(
                    minutes === 1 ? this.NEW_KEYS.DURATION_MINUTE : this.NEW_KEYS.DURATION_MINUTES
                )}`
            );
        }

        return parts.join(' ');
    }

    private static formatTime(timestamp: number, locale: string | undefined, timezone: string): string {
        if (!timestamp) {
            return '';
        }

        return new Intl.DateTimeFormat(locale || 'en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: timezone
        }).format(new Date(timestamp));
    }

    private static isDifferentMinute(first: number | undefined, second: number | undefined): boolean {
        if (!first || !second) {
            return false;
        }

        return Math.floor(first / 60000) !== Math.floor(second / 60000);
    }

    private static resolveStopNameForAria(
        name: string | undefined,
        translate: BuildJourneyAriaLabelParams['translate']
    ): string {
        if (!name) {
            return translate('LOCATION_FROM_MAP');
        }

        const normalized = name.toLowerCase();
        const looksLikeCoordinate =
            normalized.includes('user defined coordinate') || /#-?\d+(?:[.,]\d+)?#-?\d+(?:[.,]\d+)?/.test(name);
        if (looksLikeCoordinate) {
            return translate('LOCATION_FROM_MAP');
        }

        return name;
    }
}
